import { useQuery } from '@tanstack/react-query'

import { useGlobalState } from '@/hooks/useGlobalState'
import type { GetSponsorTopicsResponse } from '@/services/api/api.types'

/**
 * Hook to access topics enabled status, whether any topics are defined, and the topics data.
 * Used throughout the app for conditional rendering of topic-related UI.
 */
export function useTopicsEnabled() {
  const { api } = useGlobalState()

  const sponsorInfoQuery = useQuery({
    queryKey: ['sponsor', 'info'],
    queryFn: () => api.getSponsorInfo(),
    staleTime: 5 * 60 * 1000,
  })

  const topicsQuery = useQuery({
    queryKey: ['sponsor', 'topics'],
    queryFn: () => api.getSponsorTopics(),
    staleTime: 5 * 60 * 1000,
  })

  const topicsEnabled = sponsorInfoQuery.data?.topicsEnabled ?? false
  const topics = topicsQuery.data ?? []
  const hasDefinedTopics = topics.length > 0

  return {
    topicsEnabled,
    hasDefinedTopics,
    topics,
    isLoading: sponsorInfoQuery.isLoading || topicsQuery.isLoading,
    refetch: async () => {
      await Promise.all([sponsorInfoQuery.refetch(), topicsQuery.refetch()])
    },
  }
}

export type UseTopicsEnabledReturn = ReturnType<typeof useTopicsEnabled>
export type TopicsData = GetSponsorTopicsResponse
